import { useEffect, type ReactNode, type RefObject } from 'react';
import { createPortal } from 'react-dom';

import { cn } from '../lib/cn';

export interface ModalProps {
    open: boolean;
    onClose: () => void;
    children: ReactNode;
    /** Element to focus when the modal opens. */
    initialFocusRef?: RefObject<HTMLElement>;
    /** Optional ARIA label when the dialog has no visible title element. */
    ariaLabel?: string;
    /** ID reference for the labelling element (use this when the dialog has a title). */
    ariaLabelledBy?: string;
    /** Extra classes for the backdrop layer — e.g. `bg-black/85` to darken. */
    className?: string;
}

// Generic centered modal. Owns:
//   - Esc-to-close listener
//   - body scroll lock while open
//   - initial focus jump
//   - backdrop click → onClose (click on a child stays inside the modal as
//     long as that child stops propagation)
//
// Renders through a React portal targeting `.v2-root` (or document.body as a
// fallback). Portaling is required because feature-side wrappers like
// `position: sticky` create stacking contexts that trap the modal — without
// a portal, later DOM siblings can paint on top of `z-50`. We deliberately
// portal INTO `.v2-root` instead of `document.body` so the v2 dark-mode
// class on the ancestor wrapper still applies and the modal looks right.
//
// Children render directly inside the backdrop layer, so absolute-positioned
// chrome (e.g. a top-right close button) and a centered content panel can
// coexist. The content panel is responsible for its own `e.stopPropagation()`
// — that's deliberate so siblings like a close button can still bubble to
// the backdrop and close the modal.
export function Modal({
    open,
    onClose,
    children,
    initialFocusRef,
    ariaLabel,
    ariaLabelledBy,
    className,
}: ModalProps) {
    useEffect(() => {
        if (!open) return;
        const onKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') onClose();
        };
        document.addEventListener('keydown', onKeyDown);
        const previousOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        initialFocusRef?.current?.focus();
        return () => {
            document.removeEventListener('keydown', onKeyDown);
            document.body.style.overflow = previousOverflow;
        };
    }, [open, onClose, initialFocusRef]);

    if (!open) return null;

    if (typeof document === 'undefined') return null;
    const target = document.querySelector('.v2-root') ?? document.body;

    return createPortal(
        <div
            role="dialog"
            aria-modal="true"
            aria-label={ariaLabel}
            aria-labelledby={ariaLabelledBy}
            onClick={onClose}
            className={cn(
                'fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm',
                className,
            )}
        >
            {children}
        </div>,
        target,
    );
}
